/**
 * Pending learned weight candidate — the operator's decision surface (M3.4).
 *
 * The learning loop proposes a new W[channel][dimension] matrix as an INACTIVE
 * weight_version with set_by='learned'. It sits there until a human promotes it.
 * Before this panel there was nowhere in the admin UI that said "a candidate is
 * waiting" — you had to already know its version number to ask
 * /weights/:version/promotion-status. So it renders loud and near the top.
 *
 * Read-only by design: eligibility comes from the engine's own promotion gate,
 * and promotion itself stays the explicit action on the Weights page. This panel
 * tells you whether to go do it and why — it never does it for you. (No
 * approve/reject action exists here or in the source page, so there is nothing
 * for ActionConfirm to gate on this panel — the only mutating actions in this
 * feature set are the weights page's save/activate/rollback and the interview
 * bank's create/save/archive.)
 *
 * Ported from quill-app/quill-admin's PendingWeightCandidatePanel.tsx.
 * quillColors replaced with lifescribe theme tokens; the navigate target now
 * points at this app's mount path (/dashboard/quill/weights instead of
 * quill-admin's standalone /weights route).
 */

import React from 'react';
import { Box, Paper, Typography, Chip, Stack, Button, Divider, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ScienceIcon from '@mui/icons-material/Science';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { PendingWeightCandidate, PromotionStatus } from '../../../services/quillAdminService';

function num(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return Number.isInteger(v) ? String(v) : v.toFixed(3);
  }
  return '—';
}

const Metric: React.FC<{ label: string; value: string; emphasis?: boolean }> = ({
  label,
  value,
  emphasis,
}) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: emphasis ? 700 : 600 }}>
      {value}
    </Typography>
  </Box>
);

const PendingWeightCandidatePanel: React.FC<{
  candidate: PendingWeightCandidate | null;
  loading?: boolean;
}> = ({ candidate, loading }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  if (loading && !candidate) {
    return (
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Checking for a pending learned weight candidate…</Typography>
      </Paper>
    );
  }
  if (!candidate) return null;

  // Nothing waiting — a quiet one-liner, not a scary empty panel.
  if (candidate.pending_weight_version == null) {
    return (
      <Paper sx={{ p: 1.5, mb: 3, bgcolor: 'action.hover' }} data-testid="pending-candidate-none">
        <Typography variant="body2" color="text.secondary">
          No learned weight candidate pending. Active weight version:{' '}
          <strong>{candidate.active_weight_version ?? '—'}</strong>.
        </Typography>
      </Paper>
    );
  }

  const promo: PromotionStatus = candidate.promotion || {};
  const eligible = promo.eligible === true;

  return (
    <Paper
      elevation={3}
      data-testid="pending-candidate-panel"
      sx={{
        p: 2.5,
        mb: 3,
        borderLeft: `6px solid ${eligible ? theme.palette.success.main : theme.palette.warning.main}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <ScienceIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Learned weight candidate awaiting promotion
        </Typography>
        <Chip
          label={`v${candidate.pending_weight_version}${
            candidate.pending_set_by ? ` · ${candidate.pending_set_by}` : ''
          }`}
          size="small"
          color="primary"
          variant="outlined"
        />
        <Chip
          icon={eligible ? <CheckCircleIcon /> : <BlockIcon />}
          label={eligible ? 'Gate passed — safe to promote' : 'Gate not passed'}
          color={eligible ? 'success' : 'warning'}
          size="small"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant={eligible ? 'contained' : 'outlined'}
          endIcon={<OpenInNewIcon />}
          onClick={() => navigate('/dashboard/quill/weights')}
        >
          Review in Weights
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {typeof promo.reason === 'string' && promo.reason
          ? promo.reason
          : 'The engine could not evaluate this candidate right now.'}
      </Typography>

      <Divider sx={{ mb: 1.5 }} />

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={3}>
        <Metric label="Active version" value={num(candidate.active_weight_version)} />
        <Metric label="Candidate version" value={num(candidate.pending_weight_version)} emphasis />
        <Metric label="Candidate likeness" value={num(promo.candidateAvgLikeness)} emphasis />
        <Metric label="Active likeness" value={num(promo.activeAvgLikeness)} />
        <Metric label="Candidate harness" value={num(promo.candidateHarnessAvg)} />
        <Metric label="Active harness" value={num(promo.activeHarnessAvg)} />
        <Metric label="Direct samples" value={num(promo.sampleSize)} />
        <Metric label="Cells" value={num(candidate.pending_cell_count)} />
        <Metric
          label="Proposed"
          value={
            candidate.pending_created_at
              ? new Date(candidate.pending_created_at).toLocaleDateString()
              : '—'
          }
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Advisory only — promotion is the explicit activate action on the Weights page. "Direct
        samples" counts member + family likeness ratings collected on the candidate; the harness
        columns are the non-regression check.
      </Typography>
    </Paper>
  );
};

export default PendingWeightCandidatePanel;
